import { useEffect, useState, useRef, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { useFormik, Form, FormikProvider } from "formik";
import {
  Stack,
  TextField,
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Typography,
  CircularProgress
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import * as Yup from "yup";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import UploadPhoto from "./UploadPhoto";
import { Icon } from '@iconify/react';
import axios from "../../util/axios";
import "./PersonalDetailTab.css";
import { Context } from "../../context";
import Snackbar from '@mui/material/Snackbar';
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Tooltip from "@mui/material/Tooltip";
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { Link as RouterLink } from 'react-router-dom';

export default function PersonalDetailTab({ userDetail }) {
  const { user } = useContext(Context);
  const { setUserDetail } = user;
  const [dob, setDob] = useState(null);
  const [userProfilePhoto, setUserProfilePhoto] = useState("");
  const [countries, setCountries] = useState([]);
  const [snackBarStatus, setSnackbarStatus] = useState(false);
  const [snackBarMessage, setSnackbarMessage] = useState("");
  const [isDeleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  let navigate = useNavigate();

  // Keep a ref to userDetail so onSubmit always has the latest
  const userDetailRef = useRef(userDetail);
  useEffect(() => {
    userDetailRef.current = userDetail;
  }, [userDetail]);

  const handleDeleteDialogOpen = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
  };

  const showMessage = () => {
    setSnackbarStatus(true);
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarStatus(false);
  };

  const snackBarAction = (
    <>
      <Tooltip title="Dismiss" placement="bottom">
      <IconButton
        size="small"
        aria-label="close"
        color="inherit"
        onClick={handleCloseSnackbar}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
      </Tooltip>
    </>
  );

  const uploadImage = (binaryString) => {
    if (!binaryString) {
      return;
    }
    let addImagePayload = {
      image: binaryString,
    };
    (async function () {
      try {
        const addImage = async () => {
          console.log(process.env.REACT_APP_SERVER_URL);
          let response = await axios(
            `${process.env.REACT_APP_API_END_POINT}util/image-upload`,
            {
              method: "post",
              data: addImagePayload,
            }
          );
          if (response && response.data) {
            setUserProfilePhoto(response.data.data);
          }
        };
        await addImage();
      } catch (error) {
        console.error(error.response);
      }
    })();
  };

  const deleteAccountHandler = () => {
    (async function () {
      try {
        const url = `${process.env.REACT_APP_API_END_POINT}user/delete-account?userId=${userDetail._id}`;
        const deleteUserAccount = async () => {
          await axios(url, {
            method: "delete"
          });
          navigate("/logout");
        };
        await deleteUserAccount();
      } catch (error) {
        console.error(error.response);
      }
    })();
  }

  useEffect(() => {
    (async function () {
      try {
        const getCountryList = async () => {
          const response = await axios(
            `${process.env.REACT_APP_API_END_POINT}util/list-country`,
            {
              method: "get",
            }
          );
          const countryList = response.data.data;
          setCountries(countryList);
        };

        await getCountryList();
      } catch (error) {
        console.error(error.response);
      }
    })();
  }, []);

  const userSchema = Yup.object().shape({
    name: Yup.string().max(60, "Must be 60 characters or less").required("Name is required"),
    gender: Yup.string().max(10, "Must be 1 characters or less"),
    dob: Yup.date().nullable().typeError("Please enter a valid date"),
    language: Yup.string().max(60, "Must be 60 characters or less"),
    country: Yup.string().max(60, "Must be 60 characters or less"),
    city: Yup.string().max(60, "Must be 60 characters or less"),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: userDetail?.name || "",
      gender: userDetail?.gender || "",
      dob: userDetail?.dob || "",
      language: userDetail?.language || "",
      country: userDetail?.country || "",
      city: userDetail?.city || "",
    },
    validationSchema: userSchema,
    onSubmit: (userData, { setSubmitting }) => {
      setError("");
      setSuccessMessage("");
      (async function () {
        const currentUserDetail = userDetailRef.current;
        if (!currentUserDetail?._id) {
          setError('User ID not found. Please log out and log back in.');
          setSubmitting(false);
          return;
        }
        userData._id = currentUserDetail._id;
        userData.dob = dob;
        userData.photoId = userProfilePhoto || "";
        try {
          const url = `${process.env.REACT_APP_API_END_POINT}user/detail`;
          const response = await axios(url, {
            method: "patch",
            data: userData,
          });
          if (response?.data?.message === "Success") {
            setSuccessMessage("Profile updated successfully.");
            // Update the context so switching tabs reflects latest data
            if (response?.data?.data) {
              setUserDetail(response.data.data);
            } else {
              setUserDetail(prev => ({ ...prev, ...userData }));
            }
          } else {
            setError(response?.data?.message || "Update failed. Please try again.");
          }
          setSubmitting(false);
        } catch (err) {
          console.error(err.response);
          setError(err?.response?.data?.message || "Something went wrong. Please try again.");
          setSubmitting(false);
        }
      })();
    },
  });

  const {
    errors,
    touched,
    values,
    handleSubmit,
    isSubmitting,
    getFieldProps,
    setFieldValue,
  } = formik;

  useEffect(() => {
    let fieldToUpdate = [
      "city",
      "country",
      "dob",
      "gender",
      "language",
      "name",
    ];
    // Object.keys(userDetail).forEach((field) => {
    //     if (fieldToUpdate.includes(field)) {
    //         setFieldValue(field, userDetail[field], false);
    //     }
    // });
    setFieldValue("name", userDetail["name"], false);
    setFieldValue("language", userDetail["language"], false);
    setFieldValue("gender", userDetail["gender"], false);
    setFieldValue("dob", userDetail.dob || null, false);
    setFieldValue("country", userDetail["country"] || "", false);
    setFieldValue("city", userDetail["city"] || "", false);
    // Ensure dob state is either a valid Date string, a Date instance, or null.
    // If it's an empty string, set it to null so DatePicker won't break.
    setDob(userDetail.dob ? new Date(userDetail.dob) : null);
    setUserProfilePhoto(userDetail.photoId || "");
  }, [userDetail]);

  return (
    <>
      <FormikProvider value={formik}>
        {error && (
          <Box sx={{
            color: '#d32f2f',
            bgcolor: 'rgba(211, 47, 47, 0.08)',
            border: '1px solid rgba(211, 47, 47, 0.3)',
            borderRadius: 2,
            px: 2,
            py: 1.5,
            mb: 2,
            fontSize: '0.875rem',
            fontWeight: 500,
          }}>
            {error}
          </Box>
        )}
        {successMessage && (
          <Box sx={{
            color: '#2e7d32',
            bgcolor: 'rgba(46, 125, 50, 0.08)',
            border: '1px solid rgba(46, 125, 50, 0.3)',
            borderRadius: 2,
            px: 2,
            py: 1.5,
            mb: 2,
            fontSize: '0.875rem',
            fontWeight: 500,
          }}>
            {successMessage}
          </Box>
        )}
        <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
          <Box sx={{ flexGrow: 1 }}>
            <Grid container justifyContent="center">
              <Grid item xs={12} sm={10} md={6}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
                  <Typography variant="h5" fontWeight={600}>My Account</Typography>
                  <Button
                    variant="outlined"
                    color="error" 
                    href="/logout"
                    startIcon={<Icon icon="eva:log-out-outline" />}
                  >
                    Log Out
                  </Button>
                </Stack>
                <div className="account-image">
                  <UploadPhoto
                    setImageSrc={setUserProfilePhoto}
                    imageSrc={userProfilePhoto}
                    uploadImage={uploadImage}
                    onDeletePhoto={() => setUserProfilePhoto("")}
                  />
                </div>
                <Stack spacing={1}>
                  <Stack direction={{ xs: "column", sm: "column" }} spacing={1}>
                    <TextField
                      fullWidth
                      label="Name"
                      size="small"
                      {...getFieldProps("name")}
                      error={Boolean(touched.name && errors.name)}
                      helperText={touched.name && errors.name}
                    />
                    <FormControl
                      sx={{ m: 1, minWidth: 120 }}
                      size="small"
                      className="select-name-align"
                    >
                      <InputLabel id="demo-select-small">Gender</InputLabel>
                      <Select
                        labelId="demo-select-small"
                        id="demo-select-small"
                        label="Gender"
                        {...getFieldProps("gender")}
                      >
                        <MenuItem value="">
                          <em>None</em>
                        </MenuItem>
                        <MenuItem value="m">Male</MenuItem>
                        <MenuItem value="f">Female</MenuItem>
                        <MenuItem value="o">Other</MenuItem>
                      </Select>
                    </FormControl>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <DatePicker
                        label="Date of birth"
                        value={dob}
                        onChange={(newValue) => {
                          setDob(newValue);
                          setFieldValue('dob', newValue, true); // true to validate
                        }}
                        renderInput={(params) => (
                          <TextField 
                            {...params} 
                            size="small" 
                            error={Boolean(touched.dob && errors.dob)}
                            helperText={touched.dob && errors.dob ? String(errors.dob) : ""}
                          />
                        )}
                      />
                    </LocalizationProvider>
                    <FormControl
                      sx={{ m: 1, minWidth: 120 }}
                      size="small"
                      className="select-name-align"
                    >
                      <InputLabel id="demo-select-small">Language</InputLabel>
                      <Select
                        labelId="demo-select-small"
                        id="demo-select-small"
                        label="Language"
                        {...getFieldProps("language")}
                      >
                        <MenuItem value="">
                          <em>None</em>
                        </MenuItem>
                        <MenuItem value={1}>Hindi</MenuItem>
                        <MenuItem value={2}>English</MenuItem>
                        <MenuItem value={3}>Other</MenuItem>
                      </Select>
                    </FormControl>
                    <FormControl
                      sx={{ m: 1, minWidth: 120 }}
                      size="small"
                      className="select-name-align"
                    >
                      <InputLabel id="demo-select-small">Country</InputLabel>
                      <Select
                        labelId="demo-select-small"
                        id="demo-select-small"
                        label="Country"
                        {...getFieldProps("country")}
                      >
                        {countries &&
                          countries.map((option) => (
                            <MenuItem key={option._id} value={option._id}>
                              {option.name}
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                    <TextField
                      fullWidth
                      label="City"
                      size="small"
                      {...getFieldProps("city")}
                      error={Boolean(touched.city && errors.city)}
                      helperText={touched.city && errors.city}
                    />
                    <LoadingButton
                      type="submit"
                      variant="contained"
                      size="large"
                      fullWidth
                      loading={isSubmitting}
                      sx={{ py: 1.5 }}
                    >
                      {isSubmitting ? 'Updating...' : 'Submit'}
                    </LoadingButton>
                    <Button 
                      fullWidth
                      variant="outlined" 
                      color="error" 
                      startIcon={<Icon icon="eva:trash-2-outline" />}
                      onClick={e => handleDeleteDialogOpen()}
                    >
                      Delete account
                    </Button>
                  </Stack>
                </Stack>
              </Grid>
            </Grid>
          </Box>
        </Form>
      </FormikProvider>
      <Dialog
        open={isDeleteDialogOpen}
        onClose={handleDeleteDialogClose}
        aria-labelledby="draggable-dialog-title"
      >
        <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">
          Delete Account
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure, you want to delete your account? This process can not be undone.
          </DialogContentText>
          <DialogContentText>
            Instead, would you like to provide feedback?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button autoFocus onClick={handleDeleteDialogClose}>
            Cancel
          </Button>
          <Button onClick={handleDeleteDialogClose} to="/feedback" component={RouterLink}>Feedback</Button>
          <Button onClick={deleteAccountHandler} color="error">Delete</Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right"
        }}
        open={snackBarStatus}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        message={snackBarMessage}
        action={snackBarAction}
      />
    </>
  );
}
